import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { lumi } from '@/lib/lumi'
import { usePageMeta } from '@/hooks/usePageMeta'
import {
  HOTMART_CHECKOUT_URL,
  PRIVACIDADE_TEXTO,
  PROTOCOLO_URL,
  VIVENCIAS,
} from '@/lib/mapaCuraVivencias'
import MapaCuraResult from '@/components/MapaCuraResult'

type Step = 'capture' | 1 | 2 | 3 | 4 | 5 | 'done'
type Access = 'checking' | 'locked' | 'granted'

const ACCESS_STORAGE_KEY = 'mapa_cura_acesso'

const GOLD = '#C8923A'
const GOLD_SOFT = '#E0B870'

export default function PageMapaCuraJornada() {
  usePageMeta({
    title: 'Mapa de Cura dos 4 Elementos — Reacenda Luz',
    description:
      'Uma jornada em 5 vivências para revelar o que está guardado no seu campo e criar o seu Mapa de Cura dos 4 Elementos.',
    ogImage: 'https://static.lumi.new/76/768cd02825460eb1548b4156e8d0d987.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/mapa/',
  })

  const [step, setStep] = useState<Step>('capture')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [whatsapp, setWhatsapp] = useState('')

  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [recordId, setRecordId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [access, setAccess] = useState<Access>('checking')
  const [accessCode, setAccessCode] = useState('')
  const [mapa, setMapa] = useState('')
  const [generating, setGenerating] = useState(false)
  const [mapaError, setMapaError] = useState('')
  const topRef = useRef<HTMLDivElement>(null)

  // Acesso exclusivo: o código vem no link enviado por e-mail após a compra.
  useEffect(() => {
    let cancelled = false
    async function checkAccess() {
      const params = new URLSearchParams(window.location.search)
      const fromUrl = (params.get('acesso') || '').trim().toLowerCase()
      let stored = ''
      try {
        stored = (window.localStorage.getItem(ACCESS_STORAGE_KEY) || '').trim()
      } catch {
        stored = ''
      }
      const code = fromUrl || stored
      if (!code) {
        setAccess('locked')
        return
      }
      try {
        const result = (await lumi.functions.invoke('validateMapaAccess', {
          method: 'POST',
          body: { code },
        })) as { valid?: boolean; email?: string }
        if (cancelled) return
        if (result?.valid) {
          try {
            window.localStorage.setItem(ACCESS_STORAGE_KEY, code)
          } catch {
            // armazenamento indisponível: o link do e-mail continua funcionando
          }
          if (fromUrl) window.history.replaceState({}, '', window.location.pathname)
          setAccessCode(code)
          setAccess('granted')
          if (result.email) await restoreProgress(result.email)
        } else {
          try {
            window.localStorage.removeItem(ACCESS_STORAGE_KEY)
          } catch {
            // ignora
          }
          setAccess('locked')
        }
      } catch (err) {
        console.error('Erro ao validar acesso:', err)
        if (!cancelled) setAccess('locked')
      }
    }
    void checkAccess()
    return () => {
      cancelled = true
    }
  }, [])

  // Retoma de onde a pessoa parou, sem pedir tudo de novo.
  async function restoreProgress(userEmail: string) {
    try {
      const { list } = await lumi.entities.mapa_cura_jornada.list({
        filter: { email: userEmail.trim().toLowerCase() },
        sort: { createdAt: -1 },
        limit: 1,
      })
      const record = list?.[0]
      if (!record) return
      setRecordId(String(record._id || ''))
      if (record.name) setName(String(record.name))
      if (record.email) setEmail(String(record.email))
      if (record.whatsapp) setWhatsapp(String(record.whatsapp))

      const restored: Record<string, string> = {}
      for (const v of VIVENCIAS) {
        for (const q of v.questions) {
          const value = record[q.id]
          if (typeof value === 'string' && value.trim()) restored[q.id] = value
        }
      }
      setAnswers(prev => ({ ...restored, ...prev }))

      let next: Step = 1
      for (const v of VIVENCIAS) {
        const done = v.questions.every(q => (restored[q.id] || '').trim())
        if (done) next = v.number === VIVENCIAS.length ? 'done' : ((v.number + 1) as Step)
        else break
      }
      setStep(next)
    } catch (err) {
      console.error('Erro ao retomar a jornada:', err)
    }
  }

  // Proteção do conteúdo: sem seleção de texto e sem menu de contexto.
  useEffect(() => {
    function blockContext(e: MouseEvent) {
      e.preventDefault()
    }
    function blockSelect(e: Event) {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
      e.preventDefault()
    }
    document.addEventListener('contextmenu', blockContext)
    document.addEventListener('selectstart', blockSelect)
    return () => {
      document.removeEventListener('contextmenu', blockContext)
      document.removeEventListener('selectstart', blockSelect)
    }
  }, [])

  const vivencia = typeof step === 'number' ? VIVENCIAS[step - 1] : null
  const accent = vivencia ? vivencia.accent : GOLD
  const accentSoft = vivencia ? vivencia.accentSoft : GOLD_SOFT
  const progress = typeof step === 'number' ? (step / VIVENCIAS.length) * 100 : 0

  function updateAnswer(id: string, value: string) {
    setAnswers(prev => ({ ...prev, [id]: value }))
    setError('')
  }

  function scrollTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  async function handleCapture() {
    setError('')
    const trimName = name.trim()
    const trimEmail = email.trim()
    const trimWhats = whatsapp.trim()

    if (trimName.length < 3) {
      setError('Por favor informe seu nome completo.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
      setError('Por favor informe um e-mail válido.')
      return
    }
    if (trimWhats.replace(/\D/g, '').length < 10) {
      setError('Por favor informe seu WhatsApp com DDD.')
      return
    }

    setSaving(true)
    try {
      const now = new Date().toISOString()
      const created = await lumi.entities.mapa_cura_jornada.create({
        name: trimName,
        email: trimEmail,
        whatsapp: trimWhats,
        status: 'iniciado',
        createdAt: now,
        updatedAt: now,
      })
      setRecordId((created as { _id?: string })._id ?? '')
      setStep(1)
      scrollTop()
    } catch (err) {
      console.error('Erro ao iniciar o Mapa de Cura:', err)
      setError('Não foi possível salvar seus dados agora. Tente novamente em instantes.')
    } finally {
      setSaving(false)
    }
  }

  async function gerarMapa() {
    if (!accessCode) return
    setGenerating(true)
    setMapaError('')
    try {
      const result = (await lumi.functions.invoke('generateMapaCura', {
        method: 'POST',
        body: { code: accessCode, recordId },
      })) as { mapa?: string; error?: string }
      if (result?.mapa) {
        setMapa(result.mapa)
      } else {
        setMapaError('Não foi possível gerar o seu mapa agora. Toque no botão abaixo para tentar novamente.')
      }
    } catch (err) {
      console.error('Erro ao gerar o mapa:', err)
      setMapaError('Não foi possível gerar o seu mapa agora. Toque no botão abaixo para tentar novamente.')
    } finally {
      setGenerating(false)
    }
  }

  async function handleVivencia() {
    if (!vivencia) return
    setError('')

    const missing = vivencia.questions.find(q => !(answers[q.id] || '').trim())
    if (missing) {
      setError('Responda todas as perguntas desta vivência para seguir para a próxima.')
      document.getElementById(`field-${missing.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    setSaving(true)
    try {
      const now = new Date().toISOString()
      const payload: Record<string, string> = { updatedAt: now }
      for (const q of vivencia.questions) {
        payload[q.id] = (answers[q.id] || '').trim()
      }
      const isLast = vivencia.number === VIVENCIAS.length
      payload.status = isLast ? 'concluido' : `vivencia_${vivencia.number}_enviada`

      if (recordId) {
        await lumi.entities.mapa_cura_jornada.update(recordId, payload)
      } else {
        await lumi.entities.mapa_cura_jornada.create({
          name: name.trim(),
          email: email.trim(),
          whatsapp: whatsapp.trim(),
          createdAt: now,
          ...payload,
        })
      }

      setStep(isLast ? 'done' : ((vivencia.number + 1) as Step))
      scrollTop()
      if (isLast) void gerarMapa()
    } catch (err) {
      console.error('Erro ao salvar vivência:', err)
      setError('Não foi possível salvar suas respostas agora. Tente novamente em instantes.')
    } finally {
      setSaving(false)
    }
  }

  if (access !== 'granted') {
    return (
      <div className="min-h-screen bg-ambient select-none" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
          <p className="text-[11px] tracking-[0.35em] uppercase font-body mb-6" style={{ color: GOLD_SOFT }}>
            Reacenda Luz
          </p>
          <h1 className="font-display type-hero mb-5" style={{ color: 'var(--ink,#ece4d1)' }}>
            Mapa de Cura dos{' '}
            <span className="numerals">4</span>{' '}
            <em className="italic font-light" style={{ color: GOLD_SOFT }}>
              Elementos
            </em>
          </h1>
          {access === 'checking' ? (
            <p className="font-body type-body" style={{ color: 'rgba(236,228,209,0.7)' }} role="status">
              Verificando o seu acesso...
            </p>
          ) : (
            <>
              <p className="font-body type-body mb-8" style={{ color: 'rgba(236,228,209,0.78)' }}>
                Este é um espaço exclusivo para quem adquiriu o Mapa de Cura. Cada pessoa recebe, por e-mail, um
                link único de acesso logo após a confirmação da compra.
              </p>
              <a
                href={HOTMART_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary type-button block w-full px-6 py-4 tracking-[0.12em] uppercase no-underline"
              >
                Quero o meu Mapa de Cura
              </a>
              <p className="font-body type-label mt-8" style={{ color: 'rgba(236,228,209,0.55)' }}>
                Já comprou? Abra o link que enviamos para o seu e-mail. Confira também a caixa de spam e a
                aba de promoções.
              </p>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`min-h-screen ${vivencia ? vivencia.ambient : 'bg-ambient'} select-none`}
      style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
    >
      <div ref={topRef} />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <AnimatePresence mode="wait">
          {step === 'capture' && (
            <motion.div
              key="capture"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.7 }}
            >
              <header className="text-center mb-10 sm:mb-12">
                <p className="text-[11px] tracking-[0.35em] uppercase font-body mb-6" style={{ color: GOLD_SOFT }}>
                  Reacenda Luz
                </p>
                <h1 className="font-display type-hero mb-5" style={{ color: 'var(--ink,#ece4d1)' }}>
                  Mapa de Cura dos{' '}
                  <span className="numerals">4</span>{' '}
                  <em className="italic font-light" style={{ color: GOLD_SOFT }}>
                    Elementos
                  </em>
                </h1>
                <p className="font-body type-body" style={{ color: 'rgba(236,228,209,0.78)' }}>
                  Quatro vivências para revelar o que está preso no seu campo, um elemento por vez. Você responde
                  no seu tempo, com verdade e sem filtro.
                </p>
              </header>

              <div
                className="card-elevated rounded-lg p-5 sm:p-6 mb-8"
                style={{ borderColor: 'rgba(200,146,58,0.28)' }}
              >
                <p className="font-body type-label italic" style={{ color: 'rgba(236,228,209,0.75)' }}>
                  {PRIVACIDADE_TEXTO}
                </p>
              </div>

              <div className="space-y-6">
                <Field id="name" label="Nome completo">
                  <input
                    id="name"
                    className={`input-field w-full rounded-md px-4 py-3 font-body ${name ? 'is-filled' : ''}`}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Seu nome completo"
                    autoComplete="name"
                  />
                </Field>
                <Field id="email" label="E-mail">
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    className={`input-field w-full rounded-md px-4 py-3 font-body ${email ? 'is-filled' : ''}`}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    autoComplete="email"
                  />
                </Field>
                <Field id="whatsapp" label="WhatsApp com DDD">
                  <input
                    id="whatsapp"
                    type="tel"
                    inputMode="tel"
                    className={`input-field w-full rounded-md px-4 py-3 font-body ${whatsapp ? 'is-filled' : ''}`}
                    value={whatsapp}
                    onChange={e => setWhatsapp(e.target.value)}
                    placeholder="(00) 00000-0000"
                    autoComplete="tel"
                  />
                </Field>
              </div>

              {error && (
                <p className="font-body field-error mt-5" role="alert">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={handleCapture}
                disabled={saving}
                className="btn-primary type-button w-full mt-8 px-6 py-4 tracking-[0.12em] uppercase"
              >
                {saving ? 'Abrindo seu espaço...' : 'Começar minhas vivências'}
              </button>

              <p className="font-body type-label text-center mt-6" style={{ color: 'rgba(236,228,209,0.45)' }}>
                Seus dados ficam apenas com você.
              </p>
            </motion.div>
          )}

          {vivencia && (
            <motion.div
              key={`viv-${vivencia.number}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-9">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] tracking-[0.3em] uppercase font-body" style={{ color: accentSoft }}>
                    Vivência{' '}
                    <span className="numerals">{vivencia.number}</span>
                    {' '}de <span className="numerals">{VIVENCIAS.length}</span>
                  </p>
                  <p className="text-[11px] font-body numerals" style={{ color: 'rgba(236,228,209,0.45)' }}>
                    {Math.round(progress)}%
                  </p>
                </div>
                <div className="w-full h-[2px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg, ${accent}, ${accentSoft})` }}
                    initial={{ width: `${((vivencia.number - 1) / VIVENCIAS.length) * 100}%` }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                </div>
              </div>

              <header className="text-center mb-9">
                <div
                  className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center text-2xl"
                  style={{ background: `${accent}1f`, border: `1px solid ${accentSoft}55` }}
                  aria-hidden
                >
                  {vivencia.glyph}
                </div>
                <h2 className="font-display type-subtitle mb-4" style={{ color: 'var(--ink,#ece4d1)' }}>
                  {vivencia.title}
                </h2>
                <p className="font-body type-body" style={{ color: 'rgba(236,228,209,0.76)' }}>
                  {vivencia.intro}
                </p>
                <div
                  className="h-px w-20 mx-auto mt-7"
                  style={{ background: `linear-gradient(90deg, transparent, ${accentSoft}66, transparent)` }}
                />
              </header>

              {vivencia.questions.length === 0 && (
                <div
                  className="card-elevated rounded-lg p-6 sm:p-8 text-center mb-9"
                  style={{ borderColor: `${accent}44` }}
                >
                  <p className="font-body type-body mb-4" style={{ color: 'rgba(236,228,209,0.85)' }}>
                    Respire fundo. Coloque uma das mãos no peito e a outra no ventre.
                  </p>
                  <p className="font-body type-body mb-4" style={{ color: 'rgba(236,228,209,0.72)' }}>
                    O que você escreveu nas quatro vivências foi acolhido. Nada aqui precisa ser resolvido agora.
                    Apenas sinta o que se abriu em você e permita que o seu campo receba essa integração no seu tempo.
                  </p>
                  <p className="font-body type-label italic" style={{ color: GOLD_SOFT }}>
                    Quando estiver pronta, o seu Mapa de Cura será construído a partir da sua verdade.
                  </p>
                </div>
              )}

              <div className="space-y-7">
                {vivencia.questions.map((q, i) => (
                  <div key={q.id}>
                    <label
                      htmlFor={`field-${q.id}`}
                      className="block font-body type-card mb-3"
                      style={{ color: 'rgba(236,228,209,0.9)' }}
                    >
                      <span className="numerals mr-1" style={{ color: accentSoft }}>
                        {String(i + 1).padStart(2, '0')}.
                      </span>{' '}
                      {q.label}
                    </label>
                    <textarea
                      id={`field-${q.id}`}
                      className={`input-field answer-field w-full rounded-md ${answers[q.id] ? 'is-filled' : ''}`}
                      rows={5}
                      value={answers[q.id] || ''}
                      onChange={e => updateAnswer(q.id, e.target.value)}
                      placeholder={q.placeholder}
                      autoCapitalize="sentences"
                      autoCorrect="on"
                      spellCheck
                    />
                  </div>
                ))}
              </div>

              {error && (
                <p className="font-body field-error mt-5" role="alert">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={handleVivencia}
                disabled={saving}
                className="btn-primary type-button w-full mt-9 px-6 py-4 tracking-[0.12em] uppercase"
              >
                {saving
                  ? 'Salvando...'
                  : vivencia.number === VIVENCIAS.length
                    ? 'Receber meu Mapa de Cura'
                    : 'Enviar e abrir a próxima vivência'}
              </button>

              <p className="font-body type-label text-center mt-5" style={{ color: 'rgba(236,228,209,0.45)' }}>
                {vivencia.number === VIVENCIAS.length
                  ? 'Ao tocar no botão, o seu mapa começa a ser construído.'
                  : 'A próxima vivência só se abre depois que esta for enviada por completo.'}
              </p>
            </motion.div>
          )}

          {step === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <p className="text-[11px] tracking-[0.35em] uppercase font-body mb-6" style={{ color: GOLD_SOFT }}>
                Mapa de Cura dos 4 Elementos
              </p>
              <h2 className="font-display type-hero mb-6" style={{ color: 'var(--ink,#ece4d1)' }}>
                Suas vivências{' '}
                <em className="italic font-light" style={{ color: GOLD_SOFT }}>
                  chegaram
                </em>
              </h2>
              <p className="font-body type-body mb-4" style={{ color: 'rgba(236,228,209,0.78)' }}>
                Obrigada por se expressar com verdade. Suas respostas foram recebidas e, a partir de agora, o seu mapa
                começa a ser construído, elemento por elemento.
              </p>
              <p className="font-body type-body mb-10" style={{ color: 'rgba(236,228,209,0.6)' }}>
                Guarde o seu acesso: este mapa é seu e fica disponível apenas para você.
              </p>

              <div className="text-left mb-12">
                {generating && (
                  <div
                    className="card-elevated rounded-lg p-6 sm:p-8 text-center"
                    style={{ borderColor: 'rgba(200,146,58,0.3)' }}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="flex justify-center gap-2 mb-5" aria-hidden>
                      {[0, 1, 2].map(i => (
                        <motion.span
                          key={i}
                          className="block w-2 h-2 rounded-full"
                          style={{ background: GOLD_SOFT }}
                          animate={{ opacity: [0.25, 1, 0.25] }}
                          transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                    </div>
                    <p className="font-display type-section mb-3" style={{ color: 'var(--ink,#ece4d1)' }}>
                      Seu mapa está sendo construído
                    </p>
                    <p className="font-body type-body" style={{ color: 'rgba(236,228,209,0.7)' }}>
                      Leio cada palavra que você escreveu, elemento por elemento. Isso leva alguns instantes. Não feche
                      esta página.
                    </p>
                  </div>
                )}

                {!generating && mapaError && (
                  <div
                    className="card-elevated rounded-lg p-6 sm:p-8 text-center"
                    style={{ borderColor: 'rgba(224,123,60,0.45)' }}
                  >
                    <p className="font-body type-body mb-5" style={{ color: 'rgba(236,228,209,0.85)' }} role="alert">
                      {mapaError}
                    </p>
                    <button
                      type="button"
                      onClick={gerarMapa}
                      className="btn-primary type-button w-full px-6 py-4 tracking-[0.12em] uppercase"
                    >
                      Tentar novamente
                    </button>
                  </div>
                )}

                {!generating && !mapaError && mapa && (
                  <div className="mb-4">
                    <p
                      className="text-[11px] tracking-[0.35em] uppercase font-body mb-4 text-center"
                      style={{ color: GOLD_SOFT }}
                    >
                      Resultado do Mapa de Cura dos <span className="numerals">4</span> Elementos
                    </p>
                    <h2 className="font-display type-subtitle mb-8 text-center" style={{ color: 'var(--ink,#ece4d1)' }}>
                      {name.trim() || 'Seu mapa'}
                    </h2>
                    <MapaCuraResult text={mapa} />
                  </div>
                )}

                {!generating && !mapaError && !mapa && (
                  <div
                    className="card-elevated rounded-lg p-6 sm:p-8 text-center"
                    style={{ borderColor: 'rgba(200,146,58,0.3)' }}
                  >
                    <p className="font-body type-body mb-5" style={{ color: 'rgba(236,228,209,0.8)' }}>
                      Para ver o seu mapa, toque no botão abaixo.
                    </p>
                    <button
                      type="button"
                      onClick={gerarMapa}
                      className="btn-primary type-button w-full px-6 py-4 tracking-[0.12em] uppercase"
                    >
                      Ver meu Mapa de Cura
                    </button>
                  </div>
                )}
              </div>

              <div
                className="card-elevated rounded-lg p-6 sm:p-8 text-left"
                style={{ borderColor: 'rgba(200,146,58,0.3)' }}
              >
                <h3 className="font-display type-section mb-4" style={{ color: 'var(--ink,#ece4d1)' }}>
                  Você está pronta para ir mais fundo? Conheça o Protocolo de Liberação e Cocriação
                </h3>
                <p className="font-body type-body mb-6" style={{ color: 'rgba(236,228,209,0.72)' }}>
                  126 dias, em 6 ciclos de 21 dias, com uma sessão de liberação gravada em áudio exclusivamente para
                  você, a partir das suas memórias, das suas frases e das suas feridas.
                </p>
                <a
                  href={PROTOCOLO_URL}
                  className="btn-primary type-button block text-center w-full px-6 py-4 tracking-[0.12em] uppercase no-underline"
                >
                  Conhecer o Protocolo
                </a>
              </div>

              <p className="font-body type-label italic mt-10" style={{ color: 'rgba(236,228,209,0.5)' }}>
                Com amor e presença, Mônica Maria — Terapeuta Vibracional
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function Field({
  id,
  label,
  children,
}: {
  id: string
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={id} className="block font-body type-label mb-2" style={{ color: 'rgba(236,228,209,0.7)' }}>
        {label}
      </label>
      {children}
    </div>
  )
}
